import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI';
import InitialReviewsByCongregationTable from './Table/initialReviewsByCongregation';
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { useSelector } from 'react-redux';
import toasters from '../../../../../../../utils/toasters/Toasters';

const formatPayloadDates = (payload) => {
    if (!payload) return payload;
    
    const formattedPayload = { ...payload };
    
    if (formattedPayload.startDate) {
        const startDate = new Date(formattedPayload.startDate);
        if (!isNaN(startDate)) {
            formattedPayload.startDate = startDate.toISOString().split('T')[0];
        }
    }
    
    if (formattedPayload.endDate) {
        const endDate = new Date(formattedPayload.endDate);
        if (!isNaN(endDate)) {
            formattedPayload.endDate = endDate.toISOString().split('T')[0];
        }
    }
    
    
    return formattedPayload;
};

const InitialReviewsByCongregation = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams()
    // Fetch raw data separately to access Total
    const [rawTotal, setRawTotal] = useState(0);
    const { token } = useSelector((state) => state?.auth);
    const queryClient = useQueryClient();
    const [sort, setSort] = useState(null);
    const [tableOptions, setTableOptions] = useState({
        rowPerPages: 10,
        activePage: 0,
    });
        const [payload, setPayload] = useState(
    location.state || JSON.parse(sessionStorage.getItem('payload')) || null
    );



    useEffect(() => {
    const startDate = searchParams.get('startDate');
    const endDate = searchParams.get('endDate');
    const code = searchParams.get('code');

    if (startDate || endDate) {
        // If URL has params, use them
        const urlPayload = { startDate, endDate, code };
        sessionStorage.setItem('payload', JSON.stringify(urlPayload));
        setPayload(urlPayload);
        return;
    }

    // Fallback to location.state
    if (location.state) {
        sessionStorage.setItem('payload', JSON.stringify(location.state));
        setPayload(location.state);
        return;
    }

    // Fallback to sessionStorage
    const storedPayload = sessionStorage.getItem('payload');
    if (storedPayload) {
        setPayload(JSON.parse(storedPayload));
    }
}, [location.state, searchParams]);

    const formattedPayload = useMemo(() => {
        return formatPayloadDates({ ...payload, export: 'N' });
      }, [payload]);      

    const handleRowsPerPage = (e) => {
        setTableOptions((prev) => ({
            ...prev,
            rowPerPages: e?.target?.value,
            activePage: 0,
        }));
    };

    const handlePageClick = (event) => {
        setTableOptions((prev) => ({
            ...prev,
            activePage: event?.selected,
        }));
    };

    const handleColumnSort = async (column, sortDirection) => {
        setSort({
            columnName: column?.sortField,
            order: sortDirection,
        });
        // No need to manually invalidate - React Query will handle the sort dependency change
    };

    const getInitialReviewByCongregation = useCallback(async () => {
        let queryParams = {
            ...formattedPayload,
            page: tableOptions.activePage + 1,
            per_page: tableOptions.rowPerPages,
        };

        if (sort?.columnName && sort?.order) {
            queryParams = {
                ...queryParams,
                sort: sort.columnName,
                order: sort.order,
            };
        }

        return fetchIncidentAnalysisData(queryParams);
    }, [formattedPayload, tableOptions, sort]);

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
      } = useQuery(
        ['fetch-initial-review-by-congregation', payload, tableOptions, sort],
        getInitialReviewByCongregation,
        {
          enabled: !!payload?.startDate && !!payload?.endDate && !!payload?.code,
        }
      );
      
useEffect(() => {
    if (data && data?.length > 0) {
        // Find the count object in the data
        const countObject = data.find(item => item.incidentname_tx === 'Count');
        if (countObject) {
            setRawTotal(countObject.description_tx || 0);
        }
    }
}, [data]);
    
    const pageCount = Math.ceil(data?.Total / tableOptions.rowPerPages);

    // PDF download function
const downloadPDF = async () => {
    try {
        const isLocalReport = location.pathname.includes('LocalReviewsByCongregation');

        const response = await axios.post(
            `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
            { ...formattedPayload, export: 'N', pagination: 'false' },
            {
                headers: {
                    'Content-type': 'application/json',
                    Authorization: 'Bearer ' + token,
                },
            }
        );

        const allData = response?.data?.data?.data || [];

        if (!allData.length) {
            toasters({ type: 'warning', message: 'No data available for PDF export.' });
            return;
        }

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [297, 210],
        });

        const reportTitle = isLocalReport
            ? 'Local Reviews by Congregation Report'
            : 'Initial Reviews by Congregation Report';

        const fileName = isLocalReport
            ? 'local-reviews-by-congregation.pdf'
            : 'initial-reviews-by-congregation.pdf';

        doc.setFontSize(18);
        doc.text(reportTitle, 14, 22);

        doc.setFontSize(12);
        doc.text(
            `Date Range: ${formattedPayload.startDate} - ${formattedPayload.endDate}`,
            14,
            30
        );

        const tableColumn = [
            'Incident Name',
            'Incident Description',
            'Reviewed By',
            'Created In',
        ];

        const tableRows = allData.map((item) => [
            item?.incidentname_tx || '',
            item?.description_tx || '',
            item?.user_who_changed_status_name_tx || '',
            item?.group_name_tx || '',
        ]);

       doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 40,
    theme: 'striped',
    styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'linebreak', // Ensures text wraps within the cell
        halign: 'left'
    },
    headStyles: {
        fillColor: [41, 128, 185], // Matches your blue header
        textColor: 255,
        fontStyle: 'bold'
    },
    columnStyles: {
        0: { cellWidth: 40 }, // Incident Name
        1: { cellWidth: 'auto' }, // Incident Description (takes remaining space)
        2: { cellWidth: 35 }, // Reviewed By
        3: { cellWidth: 30 }, // Created In
    },
    // This helps break long URLs or strings that have no spaces
    didParseCell: function (data) {
        if (data.column.index === 1 && data.cell.section === 'body') {
            // Optional: Force a max width or logic for very long strings
        }
    }
});

        doc.save(fileName);
    } catch (error) {
        toasters({
            type: 'error',
            message: 'Failed to generate PDF.',
        });
    }
};


    const downloadFile = (data, fileName = 'download') => {
        
        const csvBlob = new Blob([data], { type: 'text/csv' });
    
        const csvUrl = URL.createObjectURL(csvBlob);
        const hiddenElement = document.createElement('a');
        hiddenElement.href = csvUrl;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${fileName}.csv`;
        hiddenElement.click();
    };
    
    const ExcelInitialReviewByCongregation = async () => {
        try {
            let response = await axios.post(
               ` ${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                {...formattedPayload,export:'Y', pagination: 'false'},
                {
                    headers: {
                        'Content-type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                }
            );
            downloadFile(response?.data, 'initial-reviews-congregation');
        } catch (error) {
            toasters({ type: 'error', message: error?.response?.data?.message });
        }
    };

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <InitialReviewsByCongregationTable
                isLoading={isLoading || isFetching}
                data={data?.data}
                DisplayDate={formattedPayload}
                handleColumnSort={handleColumnSort}
                tableOptions={tableOptions}
                pageCount={pageCount}
                downloadPDF={downloadPDF}
                downloadExcel={ExcelInitialReviewByCongregation}
                handlePageClick={handlePageClick}
                handleRowsPerPage={handleRowsPerPage}
                totaldata={data?.Total}
                reportData={formattedPayload}
                reportCode={payload?.code || 'initialReviewsByCongregation'}
                reportTableData={data?.data}
                generatePDFFunction={downloadPDF}
            />
            )}
        </>
    );
};

export default InitialReviewsByCongregation;
